import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { RootState } from "../store";

/**
 * Which parts of the map have finished loading.
 *
 * MUST NOT be persisted. `store.ts` persists campgroundLayer, publicLandsLayer,
 * weatherLayer and mapSettings via a redux-persist whitelist; putting readiness
 * on any of those would rehydrate it as `true` on a return visit, declaring
 * layers ready before they were and skipping the fade entirely. That is the
 * exact bug this feature exists to remove, so readiness lives in its own slice
 * and stays out of the whitelist.
 */
export type MapChoreographyState = {
  basemapReady: boolean;
  iconsReady: boolean;
  campgroundsReady: boolean;
  publicLandsReady: boolean;
  weatherReady: boolean;
};

const initialState: MapChoreographyState = {
  basemapReady: false,
  iconsReady: false,
  campgroundsReady: false,
  publicLandsReady: false,
  weatherReady: false,
};

const mapChoreography = createSlice({
  name: "mapChoreography",
  initialState,
  reducers: {
    setBasemapReady: (state, action: PayloadAction<boolean>) => {
      state.basemapReady = action.payload;
    },
    setIconsReady: (state, action: PayloadAction<boolean>) => {
      state.iconsReady = action.payload;
    },
    setCampgroundsReady: (state, action: PayloadAction<boolean>) => {
      state.campgroundsReady = action.payload;
    },
    setPublicLandsReady: (state, action: PayloadAction<boolean>) => {
      state.publicLandsReady = action.payload;
    },
    setWeatherReady: (state, action: PayloadAction<boolean>) => {
      state.weatherReady = action.payload;
    },
  },
});

export const {
  setBasemapReady,
  setIconsReady,
  setCampgroundsReady,
  setPublicLandsReady,
  setWeatherReady,
} = mapChoreography.actions;

export default mapChoreography.reducer;

const selectMapChoreography = (state: RootState) => state.mapChoreography;

export const useMapChoreographyState = (): MapChoreographyState =>
  useSelector(selectMapChoreography);
